import React, { useState, useMemo } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  TablePagination,
  IconButton,
  Tooltip,
} from '@mui/material';
import { GetApp, Visibility } from '@mui/icons-material';

interface PurchaseInvoice {
  id: string;
  order: string;
  date: string;
  customer: string;
  totalPrice: number;
}

const generateMockInvoices = (count: number): PurchaseInvoice[] => {
  const invoices: PurchaseInvoice[] = [];
  const customers = [
    'John Smith',
    'Jane Johnson',
    'Michael Williams',
    'Sarah Brown',
    'David Jones',
    'Emily Garcia',
    'Robert Miller',
    'Jessica Davis',
    'William Rodriguez',
    'Amanda Martinez',
    'James Hernandez',
    'Lisa Lopez',
    'Christopher Wilson',
    'Michelle Anderson',
    'Daniel Thomas',
    'Ashley Taylor',
  ];

  for (let i = 1; i <= count; i++) {
    const date = new Date();
    date.setDate(date.getDate() - (count - i));
    date.setHours(Math.floor(Math.random() * 24));
    date.setMinutes(Math.floor(Math.random() * 60));

    const customer = customers[Math.floor(Math.random() * customers.length)];
    const totalPrice = Math.floor(Math.random() * 2000) + 50;

    invoices.push({
      id: String(i),
      order: `ORD-2024-${String(i).padStart(4, '0')}`,
      date: date.toISOString(),
      customer,
      totalPrice,
    });
  }

  return invoices.sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );
};

const mockInvoices: PurchaseInvoice[] = generateMockInvoices(50);

const PurchaseInvoicesPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toFixed(2)}`;
  };

  const paginatedInvoices = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return mockInvoices.slice(startIndex, endIndex);
  }, [page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownload = (invoiceId: string) => {
    console.log('Download invoice:', invoiceId);
    // TODO: Implement download functionality
  };

  const handleView = (invoiceId: string) => {
    console.log('View invoice:', invoiceId);
    // TODO: Implement view functionality
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Purchase Invoices
      </Typography>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ width: '100%', overflowX: 'auto', minWidth: 0 }}>
            <TableContainer component={Paper} variant="outlined">
              <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="h6">Order</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">Date</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">Customer</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">Total price</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">Actions</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedInvoices.map(invoice => (
                  <TableRow key={invoice.id}>
                    <TableCell>{invoice.order}</TableCell>
                    <TableCell>{formatDate(invoice.date)}</TableCell>
                    <TableCell>{invoice.customer}</TableCell>
                    <TableCell>{formatCurrency(invoice.totalPrice)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleDownload(invoice.id)}
                          >
                            <GetApp />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleView(invoice.id)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={mockInvoices.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default PurchaseInvoicesPage;

